import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  convertToCurrencyFormat,
  stockUnitOfMeasureOptions,
  isReadOnlyUser,
  isManagementUser,
  getSerialFromBarcode,
  isSourceItemReturned,
  getSourceItemUnitLabel,
  mapSourceItemTaxes,
  enrichTaxesWithDropdownRates,
  sumSourceItemTaxRates,
} from "../../../../shared/utility";
import OrderSourceItemPriceSplitModal from "../OrderSourceItemPriceSplitModal";
import OrderSourceItemSplitsTable from "./OrderSourceItemSplitsTable";
import InlineSelectCell from "../../../controls/InlineSelectCell";
import { useCellSaveState } from "../../../../hooks/useCellSaveState";
import TraceableBarcode from "../../../controls/TraceableBarcode";
import TraceableLotNumber, {
  getInventoryLotTraceType,
} from "../../../controls/TraceableLotNumber";

export default function OrderSubtable1(props) {
  const {
    data,
    onItemEditComplete,
    closed,
    setEmptyModelOpen,
    showErrorModal,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    onUpdateSourceItemTax,
    pricingLocked,
    parentItemsLocked,
    onBulkUpdateSourceItemTaxIncluded,
    taxRateOptions,
  } = props;
  const [expandedRows, setExpandedRows] = useState(null);
  const [selectedSourceItemIds, setSelectedSourceItemIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkFailed, setBulkFailed] = useState(false);
  const [bulkPending, setBulkPending] = useState(false);
  const bulkPendingRef = useRef(false);
  const [lastBulkAction, setLastBulkAction] = useState(null);
  const { getSaveStatus, saveCell } = useCellSaveState();

  const isReadOnly = isReadOnlyUser();
  const isManagement = isManagementUser();
  const canAdjustTaxIncluded = (rowData) => Boolean(onBulkUpdateSourceItemTaxIncluded) &&
    !pricingLocked && !parentItemsLocked && !isSourceItemReturned(rowData);
  const eligibleItems = (data || []).filter(canAdjustTaxIncluded);
  const selectedIds = new Set(selectedSourceItemIds.map(String));
  const eligibleIds = new Set(eligibleItems.map((item) => String(item.id)));
  const eligibleIdKey = eligibleItems.map((item) => String(item.id)).join(",");
  const currentSelectedIds = eligibleItems
    .filter((item) => selectedIds.has(String(item.id)))
    .map((item) => item.id);
  const allEligibleSelected = eligibleItems.length > 0 &&
    eligibleItems.every((item) => selectedIds.has(String(item.id)));

  useEffect(() => {
    setSelectedSourceItemIds((current) => {
      const activeIds = new Set(eligibleIdKey ? eligibleIdKey.split(",") : []);
      const next = current.filter((id) => activeIds.has(String(id)));
      return next.length === current.length ? current : next;
    });
  }, [eligibleIdKey]);

  const toggleSelection = (sourceItemId, checked) => {
    setSelectedSourceItemIds((current) => {
      const next = new Set(current.map(String));
      if (checked) next.add(String(sourceItemId));
      else next.delete(String(sourceItemId));
      return Array.from(next);
    });
  };

  const setAllEligibleSelected = (checked) => {
    setSelectedSourceItemIds((current) => {
      const next = new Set(current.map(String));
      eligibleItems.forEach((item) => {
        if (checked) next.add(String(item.id));
        else next.delete(String(item.id));
      });
      return Array.from(next);
    });
  };

  const applyBulkTaxIncluded = async (taxIncluded, retry = false) => {
    if (bulkPendingRef.current || currentSelectedIds.length === 0 || !onBulkUpdateSourceItemTaxIncluded) return;
    const ids = retry && lastBulkAction
      ? lastBulkAction.ids.filter((id) => eligibleIds.has(String(id)))
      : currentSelectedIds;
    if (ids.length === 0) return;
    const nextAction = { ids: [...ids], taxIncluded };
    setLastBulkAction(nextAction);
    bulkPendingRef.current = true;
    setBulkPending(true);
    setBulkFailed(false);
    setBulkStatus("Applying…");
    try {
      const result = await onBulkUpdateSourceItemTaxIncluded(ids, taxIncluded);
      if (!result?.success) {
        setBulkFailed(true);
        setBulkStatus(result?.data?.message || "Could not update Tax Included. Your selection is kept; retry.");
        return;
      }
      const updatedIds = result.data?.updated_ids || [];
      const skippedIds = result.data?.skipped_ids || [];
      setSelectedSourceItemIds((current) => current.filter((id) =>
        !updatedIds.map(String).includes(String(id)) && !skippedIds.map(String).includes(String(id)),
      ));
      setBulkStatus(result.data?.message || `Updated ${updatedIds.length}; skipped ${skippedIds.length} tax-exempt item${skippedIds.length === 1 ? "" : "s"}.`);
    } catch {
      setBulkFailed(true);
      setBulkStatus("Could not update Tax Included. Your selection is kept; retry.");
    } finally {
      bulkPendingRef.current = false;
      setBulkPending(false);
    }
  };

  const canEditSplits = (rowData) =>
    onCreatePriceSplit &&
    onUpdatePriceSplit &&
    onDeletePriceSplit &&
    isManagement &&
    !isReadOnly &&
    !closed &&
    !isSourceItemReturned(rowData);

  const canEditTaxes = (rowData) =>
    onUpdateSourceItemTax &&
    isManagement &&
    !isReadOnly &&
    !closed &&
    !isSourceItemReturned(rowData);

  const canEditUnitType = (rowData) =>
    typeof onItemEditComplete === "function" &&
    !isReadOnly &&
    !closed &&
    !isSourceItemReturned(rowData);

  const allowExpansion = (rowData) => (rowData.price_splits || []).length > 0;

  const isRowExpanded = (rowData) =>
    Boolean(expandedRows && expandedRows[rowData.id]);

  const toggleRowExpansion = (rowData) => {
    setExpandedRows((prev) => {
      const next = { ...(prev || {}) };
      if (next[rowData.id]) {
        delete next[rowData.id];
      } else {
        next[rowData.id] = true;
      }
      return Object.keys(next).length ? next : null;
    });
  };

  const openAddSplitModal = (sourceItem) => {
    setEmptyModelOpen({
      childComponent: (
        <OrderSourceItemPriceSplitModal
          sourceItem={sourceItem}
          setOpen={setEmptyModelOpen}
          showErrorModal={showErrorModal}
          onConfirm={async (payload) => {
            const result = await onCreatePriceSplit(sourceItem.id, payload);
            if (result?.success) {
              setExpandedRows((prev) => ({
                ...(prev || {}),
                [sourceItem.id]: true,
              }));
            }
            return result;
          }}
        />
      ),
      open: true,
    });
  };

  const rowExpansionTemplate = (rowData) => (
    <OrderSourceItemSplitsTable
      sourceItem={rowData}
      setEmptyModelOpen={setEmptyModelOpen}
      showErrorModal={showErrorModal}
      onUpdatePriceSplit={onUpdatePriceSplit}
      onDeletePriceSplit={onDeletePriceSplit}
      canEditSplits={canEditSplits(rowData)}
      showSplitsColumn={
        isManagement || data?.some((row) => allowExpansion(row))
      }
      showEditorColumn={!isReadOnly}
    />
  );

  const splitActionsBody = (rowData) => {
    const showPlus = canEditSplits(rowData);
    const showExpand = allowExpansion(rowData);

    if (!showPlus && !showExpand) {
      return null;
    }

    return (
      <div className="flex items-center justify-center gap-1">
        {showPlus ? (
          <button
            type="button"
            onClick={() => openAddSplitModal(rowData)}
            className="inline-flex items-center justify-center rounded p-1 text-main-purple hover:bg-indigo-50 hover:text-indigo-700"
            title="Add price split"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        ) : null}
        {showExpand ? (
          <button
            type="button"
            onClick={() => toggleRowExpansion(rowData)}
            className="inline-flex items-center justify-center rounded p-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            title={isRowExpanded(rowData) ? "Hide splits" : "Show splits"}
          >
            {isRowExpanded(rowData) ? (
              <ChevronDownIcon className="h-5 w-5" />
            ) : (
              <ChevronRightIcon className="h-5 w-5" />
            )}
          </button>
        ) : null}
      </div>
    );
  };

  const bodyProductName = (item) => {
    const productName = item.manufactured_product?.product?.name || "";
    const plu = item.manufactured_product?.plu || "";
    const savedDiscount = item.discount_type === "percentage"
      ? `${Number(item.discount_value || 0)}% discount`
      : item.discount_type === "dollar"
        ? `${convertToCurrencyFormat(Number(item.discount_value || 0), 2)} discount`
        : null;
    return (
      <div className="flex gap-2">
        {item.is_legacy === 1 ? <p className="text-lg my-auto">L</p> : null}
        <div>
          {productName} <br /> <span className="font-bold">PLU: {plu}</span>
          {savedDiscount || item.tax_included ? (
            <div className="mt-1 flex flex-wrap gap-x-2 text-xs text-gray-600" aria-label={`Saved adjustments for ${productName}, source ${item.id}`}>
              {savedDiscount ? <span>{savedDiscount}</span> : null}
              {item.tax_included ? <span>Tax included</span> : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const selectionBody = (rowData) => {
    if (!canAdjustTaxIncluded(rowData) || !onBulkUpdateSourceItemTaxIncluded) return null;
    return (
      <label className="mx-auto flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[36px] md:min-w-[36px]">
        <input
          type="checkbox"
          aria-label={`Select ${rowData.manufactured_product?.product?.name || "source item"} source ${rowData.id} for bulk Tax Included`}
          checked={selectedIds.has(String(rowData.id))}
          disabled={bulkPending}
          onChange={(event) => toggleSelection(rowData.id, event.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </label>
    );
  };

  const bulkError = bulkFailed;
  const retryHasEligibleItems = Boolean(lastBulkAction?.ids.some((id) => eligibleIds.has(String(id))));

  const rowStyleClass = (data) => {
    if (data.credit_return_id > 0) {
      return { "bg-cyan-100": true };
    } else if (data.is_returned === 0) {
      return { "bg-gray-100": true };
    } else if (data.is_returned === 1) {
      return { "bg-yellow-100": true };
    }
    return {};
  };

  const barcodeBody = (rowData) => {
    const returned =
      rowData.credit_return_id > 0
        ? "CR"
        : rowData.is_returned === 1
          ? " RETURNED"
          : null;

    return (
      <div className="flex items-center gap-1">
        <TraceableBarcode
          barcode={rowData.manufactured_product.barcode}
          displayValue={getSerialFromBarcode(rowData.manufactured_product.barcode)}
        />
        {returned}
      </div>
    );
  };

  const lotNumberBody = (rowData) => (
    <TraceableLotNumber
      lotNumber={rowData.manufactured_product?.lot_number}
      traceType={getInventoryLotTraceType(rowData.manufactured_product)}
    />
  );

  const weightInKgBody = (rowData) => {
    return (
      <div>
        <p>{Number(rowData.net_weight_kg ?? 0).toFixed(2)} kg</p>
        <p>{Number(rowData.net_weight_lbs ?? 0).toFixed(2)} lbs</p>
      </div>
    );
  };

  const pieceCountBody = (rowData) => {
    return <p>{Number(rowData.piece_count ?? 0).toFixed(0)} pieces</p>;
  };

  const subtotalBody = (rowData) => {
    return <p>{convertToCurrencyFormat(rowData.subtotal_price, 2)}</p>;
  };

  const taxAmountBody = (rowData) => {
    return <p>{convertToCurrencyFormat(rowData.tax_amount, 2)}</p>;
  };

  const taxRatesBody = (rowData) => {
    const taxes = enrichTaxesWithDropdownRates(
      mapSourceItemTaxes(rowData),
      taxRateOptions,
    );
    const totalRate = sumSourceItemTaxRates(taxes);
    const editable = canEditTaxes(rowData);

    if (editable) {
      const cellKey = `order-source-item:${rowData.id}:tax-rates`;

      return (
        <InlineSelectCell
          ariaLabel={`Tax rates for ${
            rowData.manufactured_product?.product?.name || "source item"
          }`}
          isClearable={true}
          isMulti={true}
          name={`tax_rates_${rowData.id}`}
          onCommit={(nextTaxes) =>
            saveCell(cellKey, () =>
              onUpdateSourceItemTax(rowData.id, nextTaxes),
            )
          }
          options={taxRateOptions}
          placeholder="No tax"
          saveStatus={getSaveStatus(cellKey)}
          value={taxes.map((tax) => tax.value)}
        />
      );
    }

    return (
      <div className="space-y-0.5">
        {taxes.length === 0 ? (
          <span className="text-xs text-gray-400">No tax</span>
        ) : (
          taxes.map((tax) => (
            <div key={tax.value} className="text-xs whitespace-nowrap">
              {tax.label} ({parseFloat(tax.rate).toFixed(2)}%)
            </div>
          ))
        )}
        {taxes.length > 1 ? (
          <div className="text-xs text-gray-500">
            Total: {totalRate.toFixed(2)}%
          </div>
        ) : null}
      </div>
    );
  };

  const unitTypeBody = (rowData) => {
    const unitType = parseInt(rowData.selling_unit_of_measure, 10);
    const selectedOption = stockUnitOfMeasureOptions.find(
      (option) => option.value === unitType,
    );

    if (!canEditUnitType(rowData)) {
      const label =
        selectedOption?.label ||
        getSourceItemUnitLabel(rowData.selling_unit_of_measure) ||
        rowData.selling_unit_of_measure_name ||
        "N/A";
      return <span className="whitespace-nowrap">{label}</span>;
    }

    const cellKey = `order-source-item:${rowData.id}:selling-unit`;
    return (
      <InlineSelectCell
        value={unitType}
        options={stockUnitOfMeasureOptions}
        saveStatus={getSaveStatus(cellKey)}
        onCommit={(nextOption) => {
          const nextUnitType = parseInt(nextOption?.value, 10);

          if (!nextUnitType) return;

          const updatedItem = {
            ...rowData,
            net_weight_kg: parseFloat(rowData.net_weight_kg || 0).toFixed(2),
            net_weight_lbs: parseFloat(rowData.net_weight_lbs || 0).toFixed(2),
            piece_count: parseInt(rowData.piece_count || 0, 10),
            selling_unit_of_measure: nextUnitType,
            selling_unit_of_measure_name: nextOption.label,
          };

          return saveCell(cellKey, () => onItemEditComplete(updatedItem));
        }}
        name={`selling_unit_of_measure_${rowData.id}`}
        ariaLabel={`Unit type for ${
          rowData.manufactured_product?.product?.name || "source item"
        }`}
      />
    );
  };

  const pricingColumns = isManagement
    ? [
        {
          field: "subtotal_price",
          header: "Subtotal Price",
          body: subtotalBody,
        },
        {
          field: "tax_amount",
          header: "Subtotal Tax",
          body: taxAmountBody,
        },
      ]
    : [];

  return (
    <div className="overflow-x-auto">
      {onBulkUpdateSourceItemTaxIncluded ? (
        <div className="box-border mb-2 flex w-full min-w-0 max-w-[calc(100vw-4rem)] flex-wrap items-center gap-x-3 gap-y-2 rounded border border-gray-200 bg-gray-50 p-2 md:max-w-none">
          <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-700 md:min-h-[36px]">
            <input
              type="checkbox"
              aria-label="Select all eligible visible source items"
              checked={allEligibleSelected}
              disabled={eligibleItems.length === 0 || bulkPending}
              onChange={(event) => setAllEligibleSelected(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Select all eligible ({eligibleItems.length})
          </label>
          <span className="text-sm text-gray-600" aria-live="polite">
            {currentSelectedIds.length} selected
          </span>
          <button
            type="button"
            disabled={currentSelectedIds.length === 0 || bulkPending}
            onClick={() => applyBulkTaxIncluded(true)}
            className="min-h-[44px] rounded border border-indigo-300 bg-white px-3 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50 md:min-h-[36px]"
          >
            {bulkPending && lastBulkAction?.taxIncluded ? "Applying…" : "Apply Tax Included"}
          </button>
          <button
            type="button"
            disabled={currentSelectedIds.length === 0 || bulkPending}
            onClick={() => applyBulkTaxIncluded(false)}
            className="min-h-[44px] rounded border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 md:min-h-[36px]"
          >
            {bulkPending && lastBulkAction && !lastBulkAction.taxIncluded ? "Applying…" : "Remove Tax Included"}
          </button>
          <span role={bulkError ? "alert" : "status"} className={`min-w-0 basis-full break-words text-xs ${bulkError ? "text-red-700" : "text-gray-600"}`}>
            {bulkStatus}
          </span>
          {bulkError ? (
            <button
              type="button"
              disabled={bulkPending || !retryHasEligibleItems}
              onClick={() => applyBulkTaxIncluded(lastBulkAction?.taxIncluded, true)}
              className="min-h-[44px] text-xs text-indigo-700 underline md:min-h-[36px]"
            >Retry bulk update</button>
          ) : null}
        </div>
      ) : null}
      <DataTable
        value={data}
        dataKey="id"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm shipping-sub-items-table"
        cellClassName="py-2 border-1-bottom-gray px-2 text-sm align-middle"
        rowClassName={rowStyleClass}
      >
        {onBulkUpdateSourceItemTaxIncluded ? (
          <Column
            header="Select"
            body={selectionBody}
            headerStyle={{ minWidth: "3.5rem", width: "3.5rem" }}
            bodyStyle={{ minWidth: "3.5rem", width: "3.5rem", textAlign: "center" }}
          />
        ) : null}
        <Column
          header="Product"
          body={bodyProductName}
          headerStyle={{ minWidth: "11rem" }}
          bodyStyle={{ minWidth: "11rem", whiteSpace: "normal" }}
        />
        <Column
          header="Lot Number"
          body={lotNumberBody}
          headerStyle={{ minWidth: "7rem" }}
          bodyStyle={{ minWidth: "7rem" }}
        />
        <Column
          header="Serial"
          body={barcodeBody}
          headerStyle={{ minWidth: "6rem" }}
          bodyStyle={{ minWidth: "6rem" }}
        />
        <Column
          header="Weight"
          field="net_weight_kg"
          body={weightInKgBody}
          headerStyle={{ minWidth: "5.5rem" }}
          bodyStyle={{ minWidth: "5.5rem", textAlign: "right" }}
        />
        <Column
          header="Pieces"
          field="piece_count"
          body={pieceCountBody}
          headerStyle={{ minWidth: "4.5rem" }}
          bodyStyle={{ minWidth: "4.5rem", textAlign: "right" }}
        />
        {pricingColumns.map((col) => (
          <Column
            key={col.field}
            header={col.header}
            field={col.field}
            body={col.body}
            headerStyle={{ minWidth: "5.5rem" }}
            bodyStyle={{ minWidth: "5.5rem", textAlign: "right" }}
          />
        ))}
        {isManagement ? (
          <Column
            header="Tax rates"
            body={taxRatesBody}
            headerStyle={{ minWidth: "7rem" }}
            bodyStyle={{ minWidth: "7rem" }}
          />
        ) : null}
        <Column
          header="Unit Type"
          field="selling_unit_of_measure"
          body={unitTypeBody}
          headerStyle={{ minWidth: "5rem" }}
          bodyStyle={{ minWidth: "5rem" }}
        />
        {isManagement || data?.some((row) => allowExpansion(row)) ? (
          <Column
            header="Splits"
            body={splitActionsBody}
            headerStyle={{ minWidth: "4.5rem", width: "4.5rem" }}
            bodyStyle={{
              minWidth: "4.5rem",
              width: "4.5rem",
              textAlign: "center",
            }}
          />
        ) : null}
      </DataTable>
    </div>
  );
}
